import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

interface ConfirmDialogV2Props {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'danger'
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmDialogV2: React.FC<ConfirmDialogV2Props> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (open) {
      confirmRef.current?.focus()
    }
  }, [open])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    },
    [onCancel]
  )

  if (!open) return null

  return (
    <Overlay onClick={onCancel} onKeyDown={handleKeyDown}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>{title}</Title>
        <Message>{message}</Message>
        <ButtonRow>
          <CancelButton onClick={onCancel}>{cancelLabel}</CancelButton>
          <ConfirmButton
            ref={confirmRef}
            variant={variant}
            onClick={onConfirm}
          >
            {confirmLabel}
          </ConfirmButton>
        </ButtonRow>
      </Dialog>
    </Overlay>
  )
}

export default ConfirmDialogV2

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const Dialog = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 340px;
  max-width: 440px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
`

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #282828;
`

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`

const ConfirmButton = styled.button<{ variant: 'default' | 'danger' }>`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: ${({ variant }) =>
    variant === 'danger' ? '#e53e3e' : '#4190e0'};
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: ${({ variant }) =>
      variant === 'danger' ? '#c53030' : '#3578c0'};
  }
`
